import React, { useState, useEffect } from 'react'
import { Helmet } from 'react-helmet-async'
import { useParams, useNavigate } from 'react-router-dom'
import ArticleDetailTemplate, { type ArticleData } from '../components/templates/ArticleDetailTemplate'

const ArticleDetailPage: React.FC = () => {
  const { slug } = useParams()
  const navigate = useNavigate()
  const [article, setArticle] = useState<ArticleData | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const fetchArticle = async () => {
      if (!slug) return

      try {
        const res = await fetch(`/api/v1/articles/${slug}`)
        const foundArticle = await res.json()

        if (!res.ok || foundArticle.error) {
          throw new Error(foundArticle.error || 'Article not found')
        }

        const articleData: ArticleData = {
          id: foundArticle.id,
          slug: foundArticle.slug,
          category: foundArticle.category,
          title: foundArticle.title,
          banner_image: foundArticle.banner_image,
          content_intro: foundArticle.content_intro,
          content1: foundArticle.content1,
          content2: foundArticle.content2,
          content3: foundArticle.content3,
          content4: foundArticle.content4,
          publish_date: foundArticle.publish_date,
          location: foundArticle.location,
          desc: foundArticle.desc,
          image1: foundArticle.image1,
          image2: foundArticle.image2,
          image3: foundArticle.image3,
          image4: foundArticle.image4,
          image5: foundArticle.image5,
          image6: foundArticle.image6,
          image7: foundArticle.image7,
          image8: foundArticle.image8
        }
        setArticle(articleData)
      } catch (error) {
        console.error('Error fetching article:', error)
        setArticle(null)
      } finally {
        setLoading(false)
      }
    }

    fetchArticle()
  }, [slug])

  if (loading) {
    return (
      <main className="min-h-screen">
        <div className="fga-section">
          <div className="fga-container">
            <div className="animate-pulse">
              <div className="h-8 bg-gray-200 rounded mb-4 w-3/4"></div>
              <div className="h-4 bg-gray-200 rounded mb-2 w-full"></div>
              <div className="h-4 bg-gray-200 rounded mb-2 w-full"></div>
              <div className="h-4 bg-gray-200 rounded mb-2 w-3/4"></div>
            </div>
          </div>
        </div>
      </main>
    )
  }

  if (!article) {
    return (
      <>
        <Helmet>
          <title>Article Not Found - FGA Prestige Editorial</title>
          <meta name="description" content="The article you're looking for doesn't exist." />
        </Helmet>
        
        <main className="min-h-screen">
          <div className="fga-section">
            <div className="fga-container">
              <div className="text-center">
                <h1 className="fga-title mb-4">Article Not Found</h1>
                <p className="fga-paragraph mb-6">The article you're looking for doesn't exist.</p>
                <button
                  onClick={() => navigate('/editorial')}
                  className="fga-btn-primary"
                >
                  Back to Editorial
                </button>
              </div>
            </div>
          </div>
        </main>
      </>
    )
  }

  return (
    <>
      <Helmet>
        <title>{article.title} - FGA Prestige Editorial</title>
        <meta name="description" content={`Read ${article.title} on FGA Prestige Editorial.`} />
        <meta property="og:title" content={article.title} />
        <meta property="og:description" content={`Read ${article.title} on FGA Prestige Editorial.`} />
        <meta property="og:url" content={`https://fgaprestige.com/editorial/${article.slug}`} />
        <meta property="og:type" content="article" />
        <meta property="og:image" content={article.banner_image} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:alt" content={article.title} />
      </Helmet>
      
      <ArticleDetailTemplate article={article} />
    </>
  )
}

export default ArticleDetailPage